import { z } from 'zod';
/**
 * **Contrats d'entrée de `CDC-5` — la surface d'administration.**
 *
 * `strictObject` partout : une clé inconnue produit un 400 visible plutôt que de
 * disparaître en silence. Chaque champ porte un `.describe()`, seul moyen d'atteindre
 * OpenAPI donc le client généré (D-024).
 *
 * **CE DOCUMENT NE FAIT NAÎTRE AUCUNE ENTITÉ** (`CDC-5` §4) : il ajoute des champs sur
 * une structure qui existe, lit un réglage de parc qui naît avec le produit, et assemble
 * les objets des quatre lots précédents. **Aucune migration n'accompagne ces contrats**,
 * ce qui a été relu dans la base et non dans `schema.prisma`.
 *
 * **L'IMPORT DES COMPTES N'EST PAS ICI, ET C'EST UNE DÉCISION DATÉE.** F-66 le décrit,
 * mais son chemin est `POST /api/v1/provisionnement/import` — F-04 de `CDC-1`, livré au
 * LOT-1 et déjà ouvert aux deux rôles qui administrent. Amendement du 20260824, **D-070** :
 * « un geste, un chemin », le motif de Q566 appliqué à un sixième doublon qu'elle avait
 * manqué parce qu'il ne vivait pas dans le corpus mais dans l'écart entre le corpus et le
 * code.
 */
/** Identifiant opaque d'une ligne. Jamais interprété côté client. */
const identifiant = z.string().trim().min(1).max(64);
// ---------------------------------------------------------------------------
// F-56 — la structure comme objet administré
// ---------------------------------------------------------------------------
/**
 * **La liste des champs d'une structure est FERMÉE, et elle ne gagne rien** (Q540).
 *
 * Ce qui n'y est pas, et dont l'absence est une décision et non un oubli : **aucun champ
 * administratif** — ni numéro d'identification, ni raison sociale, ni adresse postale,
 * « rien dans le produit ne les utilise, et aucun paiement n'y entre » ; **aucune personne
 * de contact**, « la décision la plus lourde des six », parce que ce serait **une donnée
 * de personne entrant dans le produit sans compte**, la brèche exacte que la chaîne étanche
 * refuse partout ; et **aucune note libre**, « le champ où finit par entrer tout ce qui n'a
 * pas de champ, données de personnes comprises, sans classification et sans durée ».
 */
export const creerStructureSchema = z.strictObject({
    nomInterne: z
        .string()
        .trim()
        .min(1)
        .max(200)
        .describe('Le nom interne de la structure. SEUL CHAMP DE SA CRÉATION : les cinq ' +
        'réglages naissent avec elle en portant leur valeur par défaut écrite ' +
        '— aucune valeur « héritée » ni « non définie » n’existe —, et ses six ' +
        'colonnes d’affichage naissent vides, donc elle ne paraît pas sur le ' +
        'site public tant que personne ne les remplit.'),
});
/**
 * **Le compte qui voit tout modifie le nom interne. La structure ne le modifie pas.**
 *
 * C'est le seul champ modifiable par cette route : les colonnes d'affichage ont la leur,
 * les réglages la leur, et le secret ne se modifie pas — il se régénère ou se révoque.
 */
export const modifierStructureSchema = z.strictObject({
    nomInterne: z.string().trim().min(1).max(200).describe('Le nouveau nom interne.'),
});
// ---------------------------------------------------------------------------
// F-58 — les six colonnes d'affichage
// ---------------------------------------------------------------------------
/**
 * **Un couple chiffre et libellé, tel qu'il paraît sur le site public.**
 *
 * `valeur` est une **chaîne et non un nombre**, et c'est voulu : les colonnes sont « les
 * seuls champs du produit délibérément lisibles sans aucun compte », et ce qu'une
 * structure y met — « 12 ans », « +500 », « 98 % » — n'est pas un nombre à calculer mais
 * un texte à afficher. Le typer en nombre obligerait à inventer une unité.
 */
const coupleChiffre = z.strictObject({
    valeur: z.string().trim().min(1).max(24).describe('Le chiffre, tel qu’il s’affiche.'),
    libelle: z.string().trim().min(1).max(80).describe('Ce que ce chiffre désigne.'),
});
/**
 * **Les cinq colonnes de TEXTE. La sixième, le logo, est un dépôt de fichier**, et
 * voyage donc dans la partie binaire du même envoi multipart.
 *
 * **Vider une colonne se fait en envoyant une chaîne vide**, jamais en l'omettant :
 * cette route est un `PUT`, donc elle décrit l'état complet des colonnes. Une omission
 * qui vaudrait « ne change pas » ferait de ce `PUT` un `PATCH` déguisé, et **vider une
 * colonne déclencheuse est le SEUL geste qui retire une structure du site public** —
 * « aucun geste "retirer du site" n'existe, la présence étant dérivée et jamais
 * stockée ». Un geste de retrait qui dépend d'une omission ne se relit pas.
 */
/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **UN ENVOI MULTIPART NE TRANSPORTE QUE DES CHAÎNES, ET LE CONTRAT DOIT LE DIRE.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **C'est la première route multipart du produit dont le corps ne soit pas fait que de
 * chaînes**, et le défaut a été trouvé en jouant le parcours : `z.boolean()` et
 * `z.array()` refusent `'false'` et `'[{"valeur":"12"}]'`, qui sont exactement ce que le
 * transport livre. La route rendait `400` sur un corps parfaitement formé.
 *
 * **C'est la même famille que D-064**, où « un `?combien=2` arrivait en **chaîne**, et
 * `take: combien + 1` rendait `'21'` » : un canal qui n'a qu'un type, et un schéma écrit
 * comme si le corps était du JSON. **La différence est qu'ici le refus est visible** —
 * `400` plutôt qu'une valeur silencieusement fausse —, ce qui est le bon sens du défaut,
 * mais ne dispense pas de l'écrire.
 *
 * **CE QUI N'EST PAS FAIT, ET POURQUOI.** Les quatre autres routes multipart du produit
 * ne portent que des chaînes, donc ces deux aides restent **ici** plutôt que de monter
 * dans un fichier commun : une aide à un seul appelant est une indirection, pas une
 * factorisation. Elles montent le jour où une seconde route en a besoin.
 */
/** Un booléen tel qu'un formulaire le transporte, et tel qu'un client typé l'enverra. */
const booleenDeFormulaire = z
    .union([
    z.boolean(),
    z.enum(['true', 'false']).transform((brut) => brut === 'true'),
])
    .describe('Transporté en chaîne « true » ou « false » par l’envoi multipart. TOUTE ' +
    'AUTRE VALEUR EST REFUSÉE : accepter « oui », « 1 » ou une chaîne vide ' +
    'ferait décider un geste destructif par une convention non écrite.');
/** Les trois couples, transportés en une seule valeur JSON. */
const chiffresDeFormulaire = z
    .union([
    z.array(coupleChiffre).max(3),
    z.string().transform((brut, ctx) => {
        try {
            return JSON.parse(brut);
        }
        catch {
            ctx.addIssue({
                code: 'custom',
                message: 'Les chiffres voyagent en JSON dans un champ de formulaire, et ' +
                    'celui-ci n’en est pas.',
            });
            return z.NEVER;
        }
    }),
])
    .pipe(z.array(coupleChiffre).max(3))
    .describe('Jusqu’à TROIS couples chiffre et libellé, en JSON. COLONNE ' +
    'DÉCLENCHEUSE : il en faut au moins un pour que la structure paraisse ' +
    'sur le site public.');
export const colonnesAffichageSchema = z.strictObject({
    nomMarque: z
        .string()
        .trim()
        .max(120)
        .describe('Le nom d’affichage de la marque. COLONNE DÉCLENCHEUSE : vide, la ' +
        'structure ne paraît pas sur le site public.'),
    phrase: z
        .string()
        .trim()
        .max(300)
        .describe('Une phrase au nom de la STRUCTURE, jamais d’une personne.'),
    /**
     * **L'ADRESSE DE SON SITE, ET SON SCHÉMA EST CONTRAINT À `http` OU `https` — CORRIGÉ LE
     * 20260825, ET CE N'EST PAS UNE PRÉCAUTION DE STYLE.**
     *
     * **`z.url()` valide la FORME, jamais le SCHÉMA.** Il construit un `new URL()`, donc il
     * accepte `javascript:alert(1)`, `data:text/html,…`, `vbscript:` et `file:///etc/passwd`
     * — vérifié sur la version de Zod installée dans ce dépôt, pas supposé.
     *
     * **Or cette colonne devient un `href` sur la page d'accueil du produit**, F-70 : « l'adresse
     * de son propre site, **qui rend son logo cliquable** ». Et `CDC-6` §7 pose que la liste
     * blanche de la réponse publique est **le seul contrôle** de cette route : sur ce champ, elle
     * ne disait que « chaîne ».
     *
     * **Trois mécanismes l'éteignaient, et aucun n'était le contrat** — React réécrit un `href`
     * en `javascript:`, la politique de sécurité de contenu du site n'autorise pas le script en
     * ligne, et les navigateurs refusent une navigation vers `data:`. **Le serveur de
     * développement n'a que les deux premiers**, n'ayant pas la configuration nginx. Se reposer
     * sur trois couches dont aucune n'est déclarée est exactement ce que la liste blanche existe
     * pour éviter.
     */
    siteUrl: z
        .union([z.literal(''), z.url({ protocol: /^https?$/ }).max(300)])
        .describe('L’adresse de son site, en http ou https UNIQUEMENT. Chaîne vide pour la ' +
        'retirer. Elle devient un lien sur la page d’accueil publique du produit : ' +
        'tout autre schéma est refusé, y compris javascript: et data:.'),
    chiffres: chiffresDeFormulaire,
    retirerLeLogo: booleenDeFormulaire.describe('Vide la colonne du logo. Nécessaire parce qu’un envoi multipart ne sait ' +
        'pas dire « ce fichier est absent EXPRÈS » : ne pas joindre de fichier ' +
        'et vouloir retirer celui qui est là produisent le même corps.'),
});
// ---------------------------------------------------------------------------
// F-55, F-56 — la liste des structures
// ---------------------------------------------------------------------------
/**
 * `GET /api/v1/admin/structures` — F-55 et F-56, le compte qui voit tout seul.
 *
 * **C'est l'écran d'arrivée du compte qui voit tout**, F-55 : « l'écran d'arrivée est
 * asymétrique par rôle — la liste des structures pour le compte qui voit tout, l'espace
 * de sa structure pour la personne qui l'administre ». C'est aussi ce que le sélecteur
 * permanent de tête de surface consomme.
 *
 * **Pagination par curseur**, comme partout ailleurs dans ce produit : `DOCTRINE.md` §5
 * l'impose, et l'écart que le Drive a dû signaler ne se reproduit pas. **Aucun tri
 * réglable** : le sélecteur liste « toutes les structures », et un ordre unique suffit.
 */
export const structuresQuerySchema = z.strictObject({
    recherche: z
        .string()
        .trim()
        .max(200)
        .optional()
        .describe('Filtre sur le nom interne. Absent, la liste n’est pas filtrée.'),
    suite: identifiant
        .optional()
        .describe('Curseur opaque rendu par la tranche précédente. Absent, on lit depuis ' +
        'le début.'),
    combien: z.coerce
        .number()
        .int()
        .min(1)
        .max(100)
        .optional()
        .describe('Taille de la tranche. Par défaut vingt.'),
});
// ---------------------------------------------------------------------------
// F-59 — la liste des comptes et la fiche d'un compte
// ---------------------------------------------------------------------------
/**
 * `GET /api/v1/admin/structures/:id/comptes` — F-59, **les deux rôles**.
 *
 * **AUCUN TRI RÉGLABLE, ET C'EST LA RÈGLE** : « la liste des comptes rattachés EST
 * l'écran, ordonnée **par date de rattachement, du plus récent au plus ancien**, ordre
 * unique et non réglable ». Offrir un tri au contrat le rendrait exerçable, et un front
 * le brancherait.
 *
 * **CET ORDRE N'EST PAS UN CONFORT D'AFFICHAGE, IL REMPLACE UN MÉCANISME SUPPRIMÉ** :
 * « c'est cet ordre qui remplace l'**alerte email supprimée en Q114** — on voit les
 * comptes qui viennent d'être créés **en ouvrant la liste**, pas en recevant un email.
 * **Aucun objet de signalement, aucun badge, aucun compteur de nouveautés** », patron
 * exact de `CDC-4`. L'inverser retirerait donc une fonctionnalité, pas un tri.
 *
 * **QUATRE CHAMPS DE RECHERCHE, ET PAS UN DE PLUS** : nom, prénom, adresse email, et
 * identifiant de la personne chez la structure. Ce sont les valeurs que la ligne porte et
 * que la personne qui administre connaît.
 */
export const comptesQuerySchema = z.strictObject({
    recherche: z
        .string()
        .trim()
        .max(200)
        .optional()
        .describe('Filtre sur le nom, le prénom, l’adresse email et l’identifiant chez la ' +
        'structure. UN SEUL CHAMP pour les quatre : quatre filtres séparés ' +
        'seraient quatre états d’écran de plus pour la même question.'),
    suite: identifiant
        .optional()
        .describe('Curseur opaque rendu par la tranche précédente.'),
    combien: z.coerce
        .number()
        .int()
        .min(1)
        .max(100)
        .optional()
        .describe('Taille de la tranche. Par défaut vingt.'),
});
// ---------------------------------------------------------------------------
// F-60 — rattacher une personne qui existe déjà
// ---------------------------------------------------------------------------
/**
 * `POST /api/v1/admin/structures/:id/rattachements` — F-60, **les deux rôles**.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * **CE N'EST PAS UN DOUBLON DU PROVISIONNEMENT, ET L'ÉCART EST DANS F-60.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * « **Créer un compte n'est pas un geste de cette surface** : un compte naît de l'appel
 * de provisionnement ou de l'import. **Une structure rattache et détache, elle ne crée
 * pas.** » `POST /api/v1/provisionnement/comptes` **crée-ou-reconnaît** une personne par
 * son adresse ; cette route-ci prend une personne **qui existe déjà** et la rattache.
 * Deux gestes, deux routes — et la route de provisionnement n'est d'ailleurs **pas
 * ouverte à un `orgadmin`**, à qui F-60 donne celui-ci. Voir **D-071**, qui a retiré
 * trois voisines et gardé celle-là.
 *
 * **UNE STRUCTURE NE TOUCHE JAMAIS UN COMPTE, elle ne touche que des rattachements.**
 * Ce corps ne porte donc ni nom, ni prénom, ni adresse, ni rôle : « un compte appartient
 * à la personne, une structure l'atteint par le rattachement, et **changer le rôle d'une
 * personne le changerait chez toutes ses structures** ».
 */
export const rattacherSchema = z.strictObject({
    compteId: identifiant.describe('La personne à rattacher. ELLE DOIT EXISTER : cette route ne crée aucun ' +
        'compte, et une personne inconnue est refusée plutôt que créée en ' +
        'silence.'),
    identifiantExterne: z
        .string()
        .trim()
        .max(120)
        .optional()
        .describe('Son identifiant chez la structure. FACULTATIF (Q533), et vide sur le ' +
        'rattachement d’une personne qui administre.'),
});
// ---------------------------------------------------------------------------
// F-61 — les gestes réservés au compte qui voit tout sur un compte
// ---------------------------------------------------------------------------
/**
 * `PATCH /api/v1/admin/comptes/:id` — F-61, **le compte qui voit tout seul**.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * **L'ADRESSE EMAIL EST ABSENTE DE CE SCHÉMA, ET SON ABSENCE EST LA RÈGLE.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * F-61 : « **Il ne change JAMAIS une adresse email** — borne acquise au domaine des
 * accès et **non élargie ici**. » §6.14 le redit en majuscules dans la colonne des
 * interdits : « **L'ADRESSE EMAIL EST INTERDITE EN ÉCRITURE** ».
 *
 * **Et `strictObject` fait de cette absence un refus, pas un silence** : un corps qui
 * porterait `email` produit un `400` visible. Un objet permissif l'aurait ignoré, et
 * l'appelant aurait cru l'adresse changée. Le seul chemin d'une adresse est celui de son
 * titulaire — `POST /api/v1/compte/adresse`, mot de passe courant **puis** code à la
 * nouvelle adresse.
 *
 * **Trois champs facultatifs, et au moins un exigé** : un corps vide serait une écriture
 * qui ne change rien, donc une ligne de trace qui ment.
 */
export const modifierCompteSchema = z
    .strictObject({
    nom: z.string().trim().min(1).max(120).optional().describe('Son nom de famille.'),
    prenom: z.string().trim().min(1).max(120).optional().describe('Son prénom.'),
    role: z
        .enum(['TUTOR', 'STUDENT', 'ORGADMIN'])
        .optional()
        .describe('Son rôle, qui est UNIQUE et porté par le compte. « SUPERADMIN » N’EST ' +
        'PAS DANS CETTE LISTE : le compte qui voit tout « n’a jamais de ' +
        'rattachement, et il entre quand même » — en promouvoir un qui en ' +
        'porte produirait un compte que le corpus ne décrit pas. Passer à ' +
        '« ORGADMIN » exige AU PLUS UN rattachement actif (Q534).'),
})
    .refine((corps) => Object.keys(corps).length > 0, {
    message: 'Au moins un champ. Un corps vide écrirait une ligne de trace pour une ' +
        'modification qui n’a pas eu lieu.',
});
// ---------------------------------------------------------------------------
// F-62 — la liste des cours et la somme des minutes
// ---------------------------------------------------------------------------
/**
 * `GET /api/v1/admin/structures/:id/cours` — F-62, **les deux rôles**.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * **LE FILTRE DE DATES EST LA SEULE CHOSE QUI DÉCIDE QUELQUE CHOSE DANS TOUT LE
 * PRODUIT, ET F-62 LE DIT COMME ÇA.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * « **UN FILTRE DE DATES EXISTE, ET LA SOMME DES MINUTES SUIT LE FILTRE.** Sans lui, la
 * somme est un total de toujours et **aucun mois ne se facture**. **C'est la seule
 * surface du produit où un filtre de dates décide quelque chose.** »
 *
 * Partout ailleurs le corpus refuse les filtres — F-46 : « aucun filtre par date, par
 * structure ni par état ». Ici il est **obligatoire au sens fonctionnel**, et pourtant
 * facultatif au contrat : sans bornes, la somme est le total de toujours, ce qui reste
 * une lecture valide. Les rendre requises interdirait d'ouvrir l'écran.
 *
 * **AUCUN TRI RÉGLABLE**, comme partout : « une ligne par cours, du plus récent au plus
 * ancien, **ordre non réglable** ».
 */
export const coursQuerySchema = z.strictObject({
    recherche: z
        .string()
        .trim()
        .max(200)
        .optional()
        .describe('Filtre sur le PRÉNOM d’un participant et sur le TITRE du cours, et rien ' +
        'd’autre : ce sont les deux valeurs que la ligne porte et que la ' +
        'personne connaît.'),
    du: z
        .iso
        .datetime()
        .optional()
        .describe('Début de la période, ISO 8601. LA SOMME DES MINUTES SUIT CE FILTRE : ' +
        'sans lui, elle est un total de toujours et aucun mois ne se facture.'),
    au: z
        .iso
        .datetime()
        .optional()
        .describe('Fin de la période, ISO 8601. Bornes incluses.'),
    suite: identifiant.optional().describe('Curseur opaque de la tranche précédente.'),
    combien: z.coerce
        .number()
        .int()
        .min(1)
        .max(100)
        .optional()
        .describe('Taille de la tranche. Par défaut vingt.'),
});
/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LA TRANCHE DU RELEVÉ DES ENTRÉES ET DES SORTIES — D-084, ROUTE DUE DEPUIS LE
 * 20260824.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **Aucun filtre, et c'est le motif de la route qui l'interdit.** D-084 : « la liste des
 * cours affiche une durée, et **ces lignes sont ce qui la compose**. Sans elles, une
 * personne qui administre voit un nombre de minutes et **n'a aucun moyen de savoir d'où
 * il vient** — sur la seule surface du produit dont un chiffre est la base d'une
 * facturation. » Une tranche filtrée ne décomposerait plus la durée qu'elle explique :
 * **un total qu'on ne peut pas décomposer est exactement ce qu'une structure viendra
 * contester.**
 *
 * **Le filtre de dates de `coursQuerySchema` n'entre donc pas non plus.** Il existe là
 * pour que « la somme des minutes le suive, sans quoi aucun mois ne se facture » ; ici il
 * retirerait des lignes de la somme d'un seul cours.
 *
 * **La pagination reste, et elle n'est pas du confort** : `DOCTRINE.md` §5 pose « une
 * seule forme pour **toutes** les routes de liste du parc », par curseur. Et le nombre de
 * lignes d'un cours n'est pas borné — D-114 a mesuré **huit lignes pour huit
 * rechargements d'écran**, dont deux ouvertes ensemble.
 */
export const releveQuerySchema = z.strictObject({
    suite: identifiant
        .optional()
        .describe('Curseur opaque rendu par la tranche précédente.'),
    combien: z.coerce
        .number()
        .int()
        .min(1)
        .max(100)
        .optional()
        .describe('Taille de la tranche. Par défaut vingt.'),
});
// ---------------------------------------------------------------------------
// F-65 — les cinq réglages de structure, et le réglage du parc
// ---------------------------------------------------------------------------
/**
 * **La liste des cinq réglages de structure, FERMÉE** (Q536).
 *
 * « **Un sixième réglage de structure est une livraison de code.** » Cette énumération
 * est ce qui rend cette phrase vraie : une valeur hors des cinq ne franchit pas le
 * contrat, et le nom du réglage voyage **dans l'adresse**, ce qui interdit d'en poser
 * plusieurs en un appel — « aucune saisie en masse » vaut ici comme sur les colonnes.
 *
 * **Le réglage du parc n'est PAS dans cette liste, et Q551 l'écrit** : « ce n'est pas un
 * sixième réglage de structure, c'est **un nombre du produit**, porté par personne
 * d'autre, du même genre que le seuil de six flux d'image. **La liste fermée à cinq tient
 * sans changement.** » Il a ses deux routes propres.
 */
export const nomDeReglageSchema = z
    .enum([
    'poidsMaximumDunTableau',
    'dureeDeRelectureDuneScene',
    'nombreMaximumDeTableauxDunCours',
    'plafondDeVolumeDunEspace',
    'nombreMaximumDelevesParCours',
])
    .describe('LISTE FERMÉE À CINQ (Q536). Un sixième réglage de structure est une ' +
    'livraison de code, pas une valeur de plus.');
/**
 * `PUT /api/v1/admin/structures/:id/reglages/:nom` — F-65, **les deux rôles**.
 *
 * **Une seule valeur, et un seul réglage par appel.** Le nom voyage dans l'adresse : un
 * corps qui porterait les cinq ferait de chaque écriture une réécriture des quatre autres,
 * et « le dernier gagne, aucun verrou » (cas de bord 4) deviendrait alors dévastateur au
 * lieu d'être anodin — deux personnes réglant deux choses différentes s'écraseraient.
 */
export const reglageSchema = z.strictObject({
    valeur: z
        .number()
        .int()
        .min(0)
        .describe('La valeur, dans l’unité du réglage. LES BORNES SONT VÉRIFIÉES PAR LE ' +
        'SERVEUR, réglage par réglage, et LE REFUS DIT LA BORNE FRANCHIE : ' +
        'un plancher générique ici ne remplacerait aucune d’elles.'),
});
/**
 * `PUT /api/v1/admin/parc/reglage` — F-65, **le compte qui voit tout SEUL**.
 *
 * **Son unité est l'ÉLÈVE**, comme le réglage qu'il plafonne, et Q551 en donne le motif :
 * « lire *cinq participants présents* donnerait quatre élèves plus le tuteur, donc une
 * borne **plus basse que la valeur par défaut d'une structure neuve**, et la valeur
 * n'aurait aucun sens ».
 */
export const reglageDuParcSchema = z.strictObject({
    nombreMaximumElevesParCours: z
        .number()
        .int()
        .min(1)
        .describe('Le plafond du parc, EN ÉLÈVES. Il PLAFONNE le réglage d’une structure ' +
        'SANS LE REMPLACER : l’orgadmin continue de régler le sien, et le ' +
        'réglage effectif d’un cours est LE PLUS BAS DES DEUX.'),
});
// ---------------------------------------------------------------------------
// F-66 et F-67 — les exports, et les deux écrans de chiffres
// ---------------------------------------------------------------------------
/**
 * **La période, commune aux deux écrans de chiffres** — F-67.
 *
 * « Sans période, *nombre de chutes* ou *nombre de fermetures* ne veut rien dire — **et
 * le douzième indicateur le suit**. » Sur `GET /api/v1/admin/parc/chiffres`, §7 la déclare
 * **paramètre obligatoire** ; sur l'écran ouvert à la structure, elle est facultative et
 * son absence vaut « depuis toujours ».
 */
export const periodeQuerySchema = z.strictObject({
    du: z.iso.datetime().describe('Début de la période, ISO 8601. Borne incluse.'),
    au: z.iso.datetime().describe('Fin de la période, ISO 8601. Borne incluse.'),
});
/** La même période, facultative — écran ouvert à la structure. */
export const periodeFacultativeQuerySchema = z.strictObject({
    du: z.iso.datetime().optional().describe('Début de la période. Absent, depuis toujours.'),
    au: z.iso.datetime().optional().describe('Fin de la période. Absent, jusqu’à maintenant.'),
});
//# sourceMappingURL=administration.contract.js.map